import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bank } from '../types';

interface BankDropdownProps {
  banks: Bank[];
  selectedBank: string | null;
  onBankSelect: (bankId: string) => void;
  placeholder?: string;
}

const BankDropdown: React.FC<BankDropdownProps> = ({
  banks,
  selectedBank,
  onBankSelect,
  placeholder
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const placeholderText = placeholder || t('chooseBankPlaceholder', { ns: 'bankDropdown' });

  // Create virtual "Other banks" option
  const otherBankOption = {
    id: 'other',
    name: t('otherBanks', { ns: 'bankDropdown' }),
    description: 'Auto-detect bank statement format using AI',
    logo: '🤖', // Robot emoji for AI detection
    fileTypes: ['.csv']
  };

  // Combine banks with the "Other banks" option
  const allBankOptions = [...banks, otherBankOption];

  const selectedBankData = allBankOptions.find(bank => bank.id === selectedBank);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBankSelect = (bankId: string) => {
    onBankSelect(bankId);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="bank-dropdown-container" ref={dropdownRef}>
      <div 
        className={`bank-dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
      >
        {selectedBankData ? (
          <div className="selected-bank-display">
            {selectedBankData.id === 'other' && (
              <div className="bank-dropdown-logo-container">
                <span className="bank-dropdown-emoji">{selectedBankData.logo}</span>
              </div>
            )}
            <span className="bank-dropdown-name">{selectedBankData.name}</span>
          </div>
        ) : (
          <span className="bank-dropdown-placeholder">{placeholderText}</span>
        )}
        <svg 
          className={`bank-dropdown-arrow ${isOpen ? 'rotated' : ''}`}
          width="20" 
          height="20" 
          viewBox="0 0 20 20" 
          fill="none"
        >
          <path 
            stroke="currentColor" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="1.5" 
            d="m6 8 4 4 4-4"
          />
        </svg>
      </div>
      
      {isOpen && (
        <div className="bank-dropdown-menu">
          {/* Regular bank options */}
          {banks.map((bank) => (
            <div
              key={bank.id}
              className={`bank-dropdown-option ${selectedBank === bank.id ? 'selected' : ''}`}
              onClick={() => handleBankSelect(bank.id)}
            >
              <span className="bank-dropdown-name">{bank.name}</span>
              {bank.description.includes('Coming Soon') && (
                <span className="coming-soon-badge">{t('comingSoon', { ns: 'bankDropdown' })}</span>
              )}
            </div>
          ))}
          
          {/* Separator line */}
          {banks.length > 0 && (
            <div className="bank-dropdown-separator"></div>
          )}
          
          {/* Other banks option */}
          <div
            className={`bank-dropdown-option other-banks-option ${selectedBank === 'other' ? 'selected' : ''}`}
            onClick={() => handleBankSelect('other')}
          >
            <div className="bank-dropdown-logo-container">
              <span className="bank-dropdown-emoji">{otherBankOption.logo}</span>
            </div>
            <span className="bank-dropdown-name">{otherBankOption.name}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankDropdown; 